import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import TermCell from './termCell';
import IotTableChildRow from './iotTableChildRow';
import Tooltip from '../../../../form/fields/common/Tooltip';
import { StyledTr, StyledTd, GroupTd, QuantityInput } from '../css';
import {
  COLUMN_KEYS,
  resolveColumnValue,
  computeRowPricing,
  isRowComplete,
  getFormattedPricing,
  getSelectedTermPrice
} from '../util';

// A single-array (fixed) term defaults the selection to that term's name so its
// price still contributes to totals even though it renders as a static label.
const getInitialTerm = terms => {
  if (
    Array.isArray(terms) &&
    terms.length === 1 &&
    _.get(terms[0], 'fields.termName')
  ) {
    return _.get(terms[0], 'fields.termName', '');
  }
  return '';
};

const IotTableRow = ({
  entry,
  columns,
  groupMeta,
  interactive,
  selectTermPlaceholder,
  rowKey,
  savedSelection,
  savedSelections,
  onRowComplete,
  hardwareUnits,
  isRenewal
}) => {
  const fields = _.get(entry, 'fields', {});
  const terms = _.get(fields, 'terms', []);
  const hasTermData = Array.isArray(terms) && terms.length > 0;
  const isPriced = Boolean(
    fields.isPriceAddedToMonthly || fields.isPriceAddedToOneTime
  );

  const isHardwareRow = columns.some(
    column => _.get(column, 'key') === COLUMN_KEYS.MTM_UNITS
  );
  const ratePlanUnits = Number(_.get(hardwareUnits, 'ratePlanUnits', 0)) || 0;
  const mtmFloor = Number(_.get(hardwareUnits, 'mtmFloor', 0)) || 0;

  const [term, setTerm] = useState(
    _.get(savedSelection, 'term') || getInitialTerm(terms)
  );
  const [quantity, setQuantity] = useState(() =>
    String(_.get(savedSelection, 'quantity', 0))
  );
  // Only the rep's units above the auto-derived floor are held here. Keeping
  // them separate is what stops a later rate-plan change from discarding them.
  const [mtmSurplus, setMtmSurplus] = useState(
    () => Number(_.get(savedSelection, 'mtmSurplus', 0)) || 0
  );

  const mtmUnits = mtmFloor + mtmSurplus;
  const totalUnits = ratePlanUnits + mtmUnits;

  const { monthly, oneTime } = computeRowPricing(
    entry,
    isHardwareRow ? { mtmUnits, isRenewal } : { term, quantity }
  );
  const rowTotal = monthly + oneTime;

  // Track whether this row is currently represented as a completed product so a
  // transition back to incomplete withdraws it from the order (VR-8/VR-9).
  const isCompletedRef = useRef(false);

  useEffect(() => {
    if (!interactive || !onRowComplete || fields.hasChildDevices) return;

    const complete = isRowComplete(
      entry,
      { term, quantity, mtmUnits },
      columns
    );

    if (complete) {
      onRowComplete(rowKey, {
        key: rowKey,
        label: _.get(fields, 'rateplanName', ''),
        rateplanType: _.get(fields, 'rateplanType', ''),
        term,
        quantity: isHardwareRow ? mtmUnits : Number(quantity) || 0,
        ...(isHardwareRow ? { mtmSurplus } : {}),
        monthly,
        oneTime
      });
      isCompletedRef.current = true;
    } else if (isCompletedRef.current) {
      onRowComplete(rowKey, null);
      isCompletedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, quantity, mtmUnits, isRenewal]);

  const renderCell = descriptor => {
    const { key, index } = descriptor;
    const cellKey = `${rowKey}-cell-${index}`;

    if (key === COLUMN_KEYS.GROUPED) {
      if (!groupMeta || !groupMeta.shouldRenderGroupCell) return null;
      return (
        <GroupTd key={cellKey} rowSpan={groupMeta.rowSpan}>
          {_.get(fields, 'rateplanType', '')}
        </GroupTd>
      );
    }

    if (key === COLUMN_KEYS.RATE_PLAN) {
      if (fields.isThisRatePlan) {
        return (
          <StyledTd key={cellKey}>{_.get(fields, 'rateplanName', '')}</StyledTd>
        );
      }
      if (hasTermData) {
        return (
          <StyledTd key={cellKey}>
            <TermCell
              terms={terms}
              context="row"
              interactive={interactive}
              value={term}
              name={`${rowKey}-term`}
              placeholder={selectTermPlaceholder}
              onChange={setTerm}
            />
          </StyledTd>
        );
      }
      return <StyledTd key={cellKey} />;
    }

    if (key === COLUMN_KEYS.UNITS) {
      if (interactive && (hasTermData || isPriced)) {
        return (
          <StyledTd key={cellKey}>
            <QuantityInput
              type="text"
              inputMode="numeric"
              aria-label="Units"
              value={quantity}
              onChange={event => {
                const digits = event.target.value.replace(/\D/g, '');
                const cleaned = digits.replace(/^0+(?=\d)/, '');
                setQuantity(cleaned === '' ? '0' : cleaned);
              }}
            />
          </StyledTd>
        );
      }
      return (
        <StyledTd key={cellKey}>
          {Number(quantity) > 0 ? quantity : ''}
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.RATE_PLAN_UNITS) {
      // Auto-derived from committed-term plans. Rendered as text rather than a
      // disabled input so it stays announced to screen readers.
      return (
        <StyledTd key={cellKey} aria-label="Rate plan units">
          {ratePlanUnits}
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.MTM_UNITS) {
      if (!interactive) {
        return <StyledTd key={cellKey}>{mtmUnits}</StyledTd>;
      }
      return (
        <StyledTd key={cellKey}>
          <QuantityInput
            type="text"
            inputMode="numeric"
            aria-label="MTM units"
            value={String(mtmUnits)}
            onChange={event => {
              const digits = event.target.value.replace(/\D/g, '');
              const entered = digits === '' ? 0 : Number(digits);
              // Below the floor snaps back; above it becomes the rep's surplus.
              setMtmSurplus(Math.max(0, entered - mtmFloor));
            }}
          />
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.TOTAL_UNITS) {
      return (
        <StyledTd key={cellKey} aria-label="Total units">
          {totalUnits}
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.TOTAL) {
      return <StyledTd key={cellKey}>{getFormattedPricing(rowTotal)}</StyledTd>;
    }

    // For a term-bearing row the unit price comes from the selected term's
    // `termPrice` (there is no authored `fields.unitPrice`), so render the
    // selected term price instead of the static/empty authored value.
    if (key === COLUMN_KEYS.UNIT_PRICE && hasTermData) {
      return (
        <StyledTd key={cellKey}>
          {term ? getFormattedPricing(getSelectedTermPrice(terms, term)) : ''}
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.NAME) {
      const productToolTip = _.get(fields, 'toolTip', null);
      return (
        <StyledTd key={cellKey}>
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            {resolveColumnValue(entry, descriptor)}
            {productToolTip ? <Tooltip>{productToolTip}</Tooltip> : null}
          </span>
        </StyledTd>
      );
    }

    return (
      <StyledTd key={cellKey}>{resolveColumnValue(entry, descriptor)}</StyledTd>
    );
  };

  // A parent with child devices is not itself a product row: the child devices
  // are the interactive priced rows and the parent's group label spans them.
  if (fields.hasChildDevices) {
    return (
      <IotTableChildRow
        childDevices={_.get(fields, 'deviceList', [])}
        columns={columns}
        interactive={interactive}
        selectTermPlaceholder={selectTermPlaceholder}
        parentRowKey={rowKey}
        groupLabel={_.get(fields, 'rateplanName', '')}
        parentToolTip={_.get(fields, 'toolTip', null)}
        rateplanType={_.get(fields, 'rateplanType', '')}
        savedSelections={savedSelections}
        onRowComplete={onRowComplete}
      />
    );
  }

  return (
    <>
      <StyledTr>{columns.map(renderCell)}</StyledTr>
    </>
  );
};

IotTableRow.propTypes = {
  entry: PropTypes.shape({}).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  groupMeta: PropTypes.shape({
    shouldRenderGroupCell: PropTypes.bool,
    rowSpan: PropTypes.number
  }),
  interactive: PropTypes.bool,
  selectTermPlaceholder: PropTypes.string,
  rowKey: PropTypes.string.isRequired,
  savedSelection: PropTypes.shape({
    term: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mtmSurplus: PropTypes.number
  }),
  savedSelections: PropTypes.shape({}),
  onRowComplete: PropTypes.func,
  hardwareUnits: PropTypes.shape({
    ratePlanUnits: PropTypes.number,
    mtmFloor: PropTypes.number
  }),
  isRenewal: PropTypes.bool
};

IotTableRow.defaultProps = {
  groupMeta: { shouldRenderGroupCell: false, rowSpan: 1 },
  interactive: false,
  selectTermPlaceholder: '',
  savedSelection: null,
  savedSelections: {},
  onRowComplete: null,
  hardwareUnits: null,
  isRenewal: false
};

export default IotTableRow;
